/* =========================================================================
 * HUNT NOW — the per-row pushbutton. In the app, start to finish.
 *
 * Brian: "My team shouldn't have to type the championship into that search, we
 * should be able to pushbutton it and pull and record."
 *
 * So: no typing, no selection, no leaving. One click on the row you are already
 * looking at. It works out what the record needs, and if the answer is "a
 * champion", it puts the record at the front of the hand-hunt queue.
 *
 * This replaces the "Hunt in ChatGPT" control, which was the wrong answer to the
 * same problem — it composed a prompt, put it on the clipboard, sent the rep to
 * another product, and recorded nothing when they got back.
 *
 * ⚠️ THE ROUTE IS THE HAND HUNT ON THE SUBSCRIPTION. NOT A PAID API.
 *
 * Brian: "Nothing should be routed through paid api with Claude or OpenAI."
 * Brian: "the hand hunt on the subscription is the route every one of these
 * tasks must take."
 *
 * So this button does NOT call POST /api/champion-hunt. That endpoint bills per
 * record and is now switched off behind a key AND a consent flag, both absent
 * by design. Until today the row offered it as the primary action, which meant
 * the app's own front door pointed at metered spend — and when it refused, the
 * backend's message told people to set ANTHROPIC_API_KEY, i.e. the app was
 * instructing its users to open the hole.
 *
 * What it calls instead is POST /api/champion-request, which costs nothing: it
 * marks the record `sr_champion_search: 'requested'` and the hand-hunt sweep —
 * run on a subscription SR already pays for — works requested records FIRST.
 * The rep still presses one button on the row they are looking at; what changed
 * is where the work happens and what it costs.
 *
 * THE PERMISSION FOLLOWS THE COST. Asking is free, so it is gated on
 * CC_CHAMPION_UPDATE (exec + rep, own-book scoped) rather than SYSTEM_RUN
 * (exec + ops). Under the old gate a REP — the person actually working the
 * book — could not even ask for a champion to be looked up.
 *
 * NOTHING IS WRITTEN TO THE CHAMPION FIELD BY THIS BUTTON. Automation proposes;
 * the hunt records with a source, or a human types it in below.
 *
 * ⚠️ ONE BUTTON, NOT TWO. Brian: "we have fetch champion and hunt champion…
 * what the fuck." He was right. "Fetch champion" diagnosed what a record needed
 * and "Hunt champion" went and got it — a distinction that is real in the code
 * and meaningless on a row, where it read as two near-identical buttons stacked
 * with a paragraph between them.
 *
 * They are one press: it diagnoses, and unless the answer is "there is nothing
 * to find yet", it queues. The diagnosis only appears when it IS the answer —
 * an unplayed event, a record that needs fixing — rather than as a thing to
 * read before pressing the second button.
 * ========================================================================= */

import { useState } from 'react'
import { requestHunt, canHunt } from './huntJob.js'
import { api } from '../../../lib/api.js'
import { readResolveError } from './resolveModel.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import ResolvePanel from './ResolvePanel.jsx'
import { multiChampion } from './multiChampion.js'
import HuntWorkspace from './HuntWorkspace.jsx'

export { canHunt }
import styles from './pursuit.module.css'

export default function HuntNow({ record, onAccepted, compact = false }) {
  const { can, why } = useAccess()
  const [state, setState] = useState('idle') // idle | working | queued | already | diagnosed | error
  const [msg, setMsg] = useState('')
  const [diag, setDiag] = useState(null)
  const [resolving, setResolving] = useState(false)
  /* Open the resolve panel straight into SPLIT for a record that crowns many. */
  const [splitting, setSplitting] = useState(false)
  /* A refused request is a FINDING, not a failure — see below. */
  const [conflict, setConflict] = useState(null)
  /* The workspace IS the answer to the button now — see below. */
  const [hunting, setHunting] = useState(false)

  if (!canHunt(record)) return null
  const id = String(record?.id || '')
  if (!id) return null

  /* ALREADY IN THE QUEUE, READ OFF THE RECORD. The request is a HubSpot field,
   * not something this component remembers, so it survives a refresh and the
   * second rep to open the row sees that it is already asked for rather than
   * asking again. */
  const standing = String(record?.championSearchTag || '').toLowerCase() === 'requested'
  const multi = multiChampion(record)

  const run = async (e) => {
    e.stopPropagation()
    setState('working'); setMsg(''); setDiag(null); setConflict(null)
    try {
      /* STEP 1 — what does this record actually need? Cheap, read-only, and it
       * resolves the company tie where that is the whole answer. It used to be
       * its own button; now it is the first half of this one. */
      let d = null
      try { d = await api.championFetch(id) } catch { /* diagnosis is a bonus, not a gate */ }
      if (d) setDiag(d)
      /* An unplayed event has no champion to find. Queueing it would spend a
       * hunter's slot on a search that can only come back empty — or worse,
       * with last season's result. Stop here and say so. */
      if (d?.verdict === 'not_yet') { setState('diagnosed'); return }

      /* ⚠️ STEP 2 — OPEN THE HUNT, DO NOT ANNOUNCE A QUEUE.
       *
       * Brian: "I don't want it fucking queued — I want it listed just as
       * we've discussed."
       *
       * The button said FIND and set a flag. Nothing in this browser can
       * search the web — the control that could billed per record and is gone
       * by policy — so the honest version of "find" is to hand the rep the
       * record's facts, the composed search and the fields to record what they
       * find. The workspace still queues underneath as a backstop for the
       * sweep; it just is not the outcome any more, because "queued" is not an
       * answer to "who won". */
      setHunting(true)
      setState('idle')
    } catch (err) {
      /* ⚠️ A DISAGREEMENT IS A FINDING, NOT AN ERROR.
       *
       * The server refuses to queue a record that already names a champion and
       * answers 409 with that name — putting a finished record back in the
       * hunt queue is how a good crown gets overwritten. Rendering that as a
       * red string loses the question it is actually asking, which is "is the
       * champion on this record wrong?". It opens the flag instead, with the
       * correction route already underneath it. */
      const refusal = readResolveError(err)
      if (refusal.kind === 'conflict') { setConflict(refusal); setState('idle'); return }
      /* Everything else in the server's own words — "failed" is not actionable
       * and these messages are. */
      setState('error')
      setMsg(err?.body?.reason || err?.body?.refusedBecause || err?.message || 'Could not queue it.')
    }
  }

  /* Asking costs nothing and writes no champion, so it rides the same
   * permission as recording one: exec + rep, scoped to the rep's own book. The
   * server re-decides it — this is a DRAWING decision (hard rule 5). */
  const mayAsk = can(ACTIONS.CC_CHAMPION_UPDATE, record)
  const asked = state === 'queued' || state === 'already' || (standing && state === 'idle')

  return (
    <div className={styles.huntNow} onClick={(e) => e.stopPropagation()}>
      {!mayAsk && (
        <div className={styles.huntNowGap} title={why(ACTIONS.CC_CHAMPION_UPDATE, record) || ''}>
          {why(ACTIONS.CC_CHAMPION_UPDATE, record) || 'Champion changes are not available for your role.'}
        </div>
      )}

      {mayAsk && (hunting ? (
        <HuntWorkspace
          record={record}
          onClose={() => setHunting(false)}
          onResolved={(rid, patch) => { setHunting(false); onAccepted && onAccepted(rid, patch) }}
        />
      ) : asked ? (
        /* A STANDING REQUEST IS NOT A RESULT. It says the sweep will also look,
         * and offers the way to go and look now — never as the whole answer. */
        <div className={styles.huntNowQueued}>
          ✓ With the hunter — runs every 15 min.{' '}
          <button type="button" className={styles.huntNowManual} onClick={() => setHunting(true)}>
            Find it now
          </button>
        </div>
      ) : state !== 'diagnosed' && (
        <button
          type="button"
          className={`${styles.huntNowBtn} ${compact ? styles.huntNowBtnCompact : ''}`}
          onClick={run}
          disabled={state === 'working'}
          title="Hand this record to the hunter — it runs every 15 minutes on the subscription SR already pays for, costs nothing per record, and takes requested records first"
        >
          {state === 'working' ? 'Checking…' : state === 'error' ? 'Try again' : 'Find champion'}
        </button>
      ))}

      {/* The diagnosis only shows when it IS the answer. */}
      {state === 'diagnosed' && diag && (
        <div className={styles.huntNowMiss}>
          <b>{diag.headline}</b>
          {diag.detail ? <div>{diag.detail}</div> : null}
        </div>
      )}

      {state === 'error' && <div className={styles.huntNowErr}>{msg}</div>}

      {/* THE WAY IN WHEN THERE IS NO LINK. Brian: "if we have to manually type
          the winner in because that's the only source". Offered beside the
          queue and after a diagnosis — never as the first thing, because a
          typed champion should be the answer of last resort, not of least
          effort. */}
      {/* ⚠️ THE DOOR FOR A MULTI-CHAMPION RECORD. Brian: "For multiple
          champions at an event, there should be record of all of them."
          Without this the row states the problem and offers only a field built
          for one name — which is how one weight-class winner gets typed in as
          THE champion. */}
      {mayAsk && multi && !conflict && !resolving && !splitting && (
        <button type="button" className={styles.huntNowManual} onClick={() => setSplitting(true)}>
          Record all {multi.count ? `${multi.count} ` : ''}{multi.plural}
          {multi.team ? ' + the team title' : ''}
        </button>
      )}

      {mayAsk && (conflict || resolving || splitting) && (
        <ResolvePanel
          record={record}
          conflict={conflict}
          seedDivisions={null}
          seedSource=""
          startMode={splitting ? 'split' : ''}
          /* 🔴 DO NOT UNMOUNT THE PANEL ON SUCCESS — THAT WAS THE BUG.
           *
           * This used to call `setResolving(false)` here, which tore the panel
           * down in the same tick its own `status: 'done'` was set. So the
           * confirmation never rendered: the live test submitted a real
           * champion, the form simply vanished, and the drawer behind it still
           * read "NO CHAMPION YET". I only knew the write had landed by
           * querying the API afterwards.
           *
           * ⚠️ A REP WHO SEES NO ANSWER SUBMITS AGAIN. That is the actual cost
           * — a second write against a record the first one just locked, which
           * comes back as a conflict and reads as the app being broken. The
           * panel now stays up, shows what it wrote, and closes when the rep
           * says so. */
          onResolved={(rid, patch) => {
            setConflict(null)
            /* The board still repaints immediately — the patch carries the
             * NORMALIZED keys the store holds, so the champion appears on the
             * row without waiting for a refetch. */
            onAccepted && onAccepted(rid, patch)
          }}
          onClose={() => { setConflict(null); setResolving(false); setSplitting(false) }}
        />
      )}

      {/* MULTIPLE WINNERS. Brian: "if it says there are multiple winners, there
          needs to be a new winner created." The split lives in the panel's own
          mode chips rather than behind a hunt result — a rep who is looking at
          a record that plainly holds two divisions should not have to run
          anything first to be allowed to say so. */}
      {mayAsk && !conflict && !resolving && (
        <button type="button" className={styles.huntNowManual} onClick={() => setResolving(true)}>
          Enter it myself
        </button>
      )}
    </div>
  )
}
